import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useCreateBillingCycle } from '@/hooks/use-billing'
import { getApiErrorMessage } from '@/lib/api'
import {
  currentMonthValue,
  FIELD_CLS,
  formatBillingPeriod,
  type Cycle,
} from './billing-cycle-shared'
import type { UtilityType } from '@riverpark/types'

export function NewPeriodModal({
  utilityType,
  cycles,
  onClose,
  onCreate,
  onOpenExisting,
}: {
  utilityType: UtilityType
  cycles: Cycle[]
  onClose: () => void
  onCreate: (period: string) => void
  onOpenExisting: (period: string) => void
}) {
  const [period, setPeriod] = useState(currentMonthValue())
  const [error, setError] = useState<string | null>(null)
  const createCycle = useCreateBillingCycle()
  const existing = cycles.find((c) => c.period === period)

  async function handleCreate() {
    setError(null)
    try {
      await createCycle.mutateAsync({ utilityTypeId: utilityType.id, billingPeriod: period })
      onCreate(period)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create billing period'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="new-period-title" panelClassName="sm:max-w-sm">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              {utilityType.name}
            </p>
            <h2 id="new-period-title" className="font-serif text-xl text-ink">
              New billing period
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>
      <ModalBody className="space-y-3">
        <div className="space-y-1.5">
          <label className="font-sans text-sm font-medium text-ink">Billing period</label>
          <input
            type="month"
            className={FIELD_CLS}
            value={period}
            max={currentMonthValue()}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </div>
        {existing && (
          <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-xs text-muted-foreground">
            <span className="font-medium text-ink">
              {utilityType.name} — {formatBillingPeriod(period)}
            </span>{' '}
            already exists.
          </p>
        )}
        {error && (
          <p className="font-sans text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {existing ? (
          <Button onClick={() => onOpenExisting(period)}>Open it</Button>
        ) : (
          <Button loading={createCycle.isPending} onClick={() => void handleCreate()}>
            Create
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}
